/**
 * CMS Page Configuration
 * Extracted from PageContentManager.jsx as part of P2 tech-debt refactor.
 *
 * Defines:
 *  - PAGE_CONFIG: top-level catalog of editable pages and their sections
 *  - SECTION_LABELS: human-readable labels for each section key
 *  - getDefaultContentForSection: returns the empty shape for a section key
 *    (used when admin clicks "Initialize Section")
 */
import {
  Home,
  Info,
  Briefcase,
  Phone,
  Globe,
  Settings,
  Award,
  GraduationCap,
  BookOpen,
  Calendar
} from 'lucide-react';

export const PAGE_CONFIG = {
  homepage: {
    label: 'Homepage',
    icon: Home,
    color: 'from-cyan-500 to-blue-500',
    previewUrl: '/',
    sections: ['hero', 'hero_stat_cards', 'section_headers', 'destinations', 'final_cta', 'seo']
  },
  about: {
    label: 'About Us',
    icon: Info,
    color: 'from-violet-500 to-purple-500',
    previewUrl: '/about',
    sections: ['hero', 'stats', 'story', 'timeline', 'values', 'leadership_header', 'values_header', 'culture_header', 'life_photos', 'gptw_banner', 'careers_cta', 'office', 'final_cta', 'seo']
  },
  services: {
    label: 'Services',
    icon: Briefcase,
    color: 'from-amber-500 to-orange-500',
    previewUrl: '/services',
    sections: ['hero', 'services', 'process_steps', 'why_choose_us', 'cta', 'seo']
  },
  contact: {
    label: 'Contact',
    icon: Phone,
    color: 'from-emerald-500 to-teal-500',
    previewUrl: '/contact',
    sections: ['hero', 'office', 'social_links', 'seo']
  },
  'destinations-list': {
    label: 'Destinations',
    icon: Globe,
    color: 'from-rose-500 to-pink-500',
    previewUrl: '/destinations',
    sections: ['hero', 'global_stats', 'destinations_data', 'section_header', 'cta', 'seo']
  },
  'student-services': {
    label: 'Student Services',
    icon: Briefcase,
    color: 'from-teal-500 to-cyan-500',
    previewUrl: '/student-services',
    sections: ['hero', 'student_services_list', 'help_cta', 'seo']
  },
  'success-stories': {
    label: 'Success Stories',
    icon: Award,
    color: 'from-amber-500 to-rose-500',
    previewUrl: '/success-stories',
    sections: ['hero', 'featured_header', 'all_stories_header', 'final_cta', 'seo']
  },
  'programs-list': {
    label: 'Programs (List Page)',
    icon: GraduationCap,
    color: 'from-indigo-500 to-blue-500',
    previewUrl: '/programs',
    sections: ['hero', 'empty_state', 'final_cta', 'seo']
  },
  'programs-detail': {
    label: 'Programs (Detail Template)',
    icon: BookOpen,
    color: 'from-fuchsia-500 to-pink-500',
    // Resolved dynamically from /api/programs/public first item
    previewUrlSample: { endpoint: '/api/programs/public', pathPrefix: '/programs/', slugKey: 'slug' },
    sections: ['template_strings', 'final_cta']
  },
  'events-list': {
    label: 'Events (List Page)',
    icon: Calendar,
    color: 'from-coral-500 to-amber-500',
    previewUrl: '/events',
    sections: ['hero', 'past_header', 'empty_state', 'newsletter_cta', 'seo']
  },
  'events-detail': {
    label: 'Events (Detail Template)',
    icon: Calendar,
    color: 'from-amber-500 to-orange-500',
    previewUrlSample: { endpoint: '/api/public/events', pathPrefix: '/events/', slugKey: 'id' },
    sections: ['template_strings', 'final_cta']
  },
  'blog-list': {
    label: 'Blog (List Page)',
    icon: BookOpen,
    color: 'from-cyan-500 to-violet-500',
    previewUrl: '/blog',
    sections: ['hero', 'featured_header', 'trending_header', 'newsletter_cta', 'tags_card', 'seo']
  },
  'blog-detail': {
    label: 'Blog (Detail Template)',
    icon: BookOpen,
    color: 'from-violet-500 to-cyan-500',
    previewUrlSample: { endpoint: '/api/public/blog', pathPrefix: '/blog/', slugKey: 'slug' },
    sections: ['template_strings', 'final_cta']
  },
  'university-detail': {
    label: 'University (Detail Template)',
    icon: Award,
    color: 'from-blue-500 to-cyan-500',
    previewUrlSample: { endpoint: '/api/universities/featured', pathPrefix: '/university/', slugKey: 'slug' },
    sections: ['template_strings', 'final_cta']
  },
  'destination-usa': { label: 'Destination · USA 🇺🇸', icon: Globe, color: 'from-red-500 to-blue-600', previewUrl: '/destinations/usa', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-uk': { label: 'Destination · UK 🇬🇧', icon: Globe, color: 'from-red-500 to-blue-600', previewUrl: '/destinations/uk', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-canada': { label: 'Destination · Canada 🇨🇦', icon: Globe, color: 'from-red-500 to-red-700', previewUrl: '/destinations/canada', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-australia': { label: 'Destination · Australia 🇦🇺', icon: Globe, color: 'from-blue-600 to-yellow-500', previewUrl: '/destinations/australia', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-germany': { label: 'Destination · Germany 🇩🇪', icon: Globe, color: 'from-yellow-500 to-red-600', previewUrl: '/destinations/germany', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-new-zealand': { label: 'Destination · New Zealand 🇳🇿', icon: Globe, color: 'from-blue-600 to-green-600', previewUrl: '/destinations/new-zealand', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-france': { label: 'Destination · France 🇫🇷', icon: Globe, color: 'from-blue-700 to-red-600', previewUrl: '/destinations/france', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-ireland': { label: 'Destination · Ireland 🇮🇪', icon: Globe, color: 'from-green-600 to-orange-500', previewUrl: '/destinations/ireland', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-singapore': { label: 'Destination · Singapore 🇸🇬', icon: Globe, color: 'from-red-600 to-white', previewUrl: '/destinations/singapore', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-dubai': { label: 'Destination · Dubai 🇦🇪', icon: Globe, color: 'from-green-600 to-red-600', previewUrl: '/destinations/dubai', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'destination-malaysia': { label: 'Destination · Malaysia 🇲🇾', icon: Globe, color: 'from-red-600 to-yellow-500', previewUrl: '/destinations/malaysia', sections: ['destination_hero', 'destination_stats', 'destination_costs', 'destination_requirements', 'whyStudy', 'popularCourses', 'scholarships', 'intakes', 'topUniversities', 'seo'] },
  'global-settings': {
    label: 'Global Settings',
    icon: Settings,
    color: 'from-slate-500 to-gray-500',
    previewUrl: '/',  // Global header/footer changes are visible on homepage
    sections: ['company_info', 'company_stats', 'cta_labels', 'social_links', 'footer_links', 'gptw_badge']
  }
};

export const SECTION_LABELS = {
  hero: 'Hero Section',
  stats: 'Statistics',
  story: 'Our Story',
  timeline: 'Application Timeline (label · activity)',
  values: 'Our Values',
  life_photos: 'Life at UniXperts Gallery',
  careers_cta: 'Careers CTA',
  office: 'Office Details',
  final_cta: 'Final CTA',
  seo: 'SEO Settings',
  destinations: 'Destinations Carousel',
  cta: 'Call to Action',
  services: 'Services List',
  process_steps: 'Process Steps',
  why_choose_us: 'Why Choose Us',
  company_stats: 'Company Stats',
  branch_offices: 'Branch Offices',
  global_stats: 'Global Statistics',
  destinations_data: 'Destinations Data',
  section_header: 'Section Header',
  company_info: 'Company Information',
  social_links: 'Social Media Links',
  footer_links: 'Footer Links',
  gptw_badge: 'GPTW Badge',
  cta_labels: 'Global CTA Labels',
  section_headers: 'Section Headers',
  hero_stat_cards: 'Hero Stat Cards',
  student_services_list: 'Student Services List',
  help_cta: 'Need Help Choosing? CTA',
  featured_header: 'Featured Story Section Header',
  all_stories_header: 'All Stories Section Header',
  leadership_header: 'Leadership Section Header',
  values_header: 'Values Section Header',
  culture_header: 'Life @ UniXperts Section Header',
  gptw_banner: 'GPTW Banner (Life @ section)',
  empty_state: 'Empty State Messages',
  template_strings: 'Page Template Strings',
  destination_hero: 'Destination Hero (name, flag, tagline, hero image)',
  destination_stats: 'Destination Stats (universities, intl students, tuition, work visa)',
  destination_costs: 'Costs (tuition range, living, total)',
  destination_requirements: 'Admission Requirements (academic, tests, documents)',
  whyStudy: 'Why Study Here (cards)',
  popularCourses: 'Popular Courses (list)',
  scholarships: 'Scholarships',
  intakes: 'Intake Cycles',
  topUniversities: 'Top Universities',
  past_header: 'Past Events Section Header',
  newsletter_cta: 'Newsletter / Subscribe CTA',
  trending_header: 'Trending Section Header',
  tags_card: 'Sidebar Tags Card'
};

/**
 * Returns the default empty content shape for a section key.
 * Used by the "Initialize Section" button when a section has no content yet.
 */
export const getDefaultContentForSection = (sectionKey) => {
  switch (sectionKey) {
    case 'hero':
      return { badge_text: '', title: '', title_highlight: '', subtitle: '', cta_primary_text: '', cta_primary_link: '' };
    case 'seo':
      return { title: '', description: '', keywords: '' };
    case 'cta':
    case 'final_cta':
    case 'careers_cta':
      return { title: '', subtitle: '', cta_primary_text: '', cta_primary_link: '', cta_secondary_text: '', cta_secondary_link: '' };
    case 'story':
      return { badge: '', title: '', title_highlight: '', paragraphs: [] };
    case 'stats':
    case 'global_stats':
    case 'company_stats':
    case 'timeline':
    case 'values':
    case 'branch_offices':
    case 'destinations':
    case 'services':
    case 'process_steps':
    case 'life_photos':
    case 'hero_stat_cards':
    case 'student_services_list':
    case 'whyStudy':
    case 'popularCourses':
    case 'scholarships':
    case 'intakes':
    case 'topUniversities':
      return [];
    case 'company_info':
    case 'office':
      return { name: '', tagline: '', description: '', phone: '', email: '', address: '' };
    case 'social_links':
    case 'footer_links':
    case 'gptw_badge':
    case 'gptw_banner':
    case 'help_cta':
    case 'empty_state':
    case 'template_strings':
    case 'section_header':
    case 'section_headers':
    case 'featured_header':
    case 'all_stories_header':
    case 'leadership_header':
    case 'values_header':
    case 'culture_header':
    case 'cta_labels':
    case 'why_choose_us':
    case 'destination_hero':
    case 'destination_stats':
    case 'destination_costs':
    case 'destination_requirements':
    case 'past_header':
    case 'newsletter_cta':
    case 'trending_header':
    case 'tags_card':
      return {};
    default:
      return {};
  }
};
